import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup,
  CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cestService } from "@/features/fiscal/services/fiscalService";

interface Props {
  value?:       string | null;   // código CEST
  onChange:     (codigo: string | null) => void;
  placeholder?: string;
  disabled?:    boolean;
}

export function CestSearchCombobox({
  value,
  onChange,
  placeholder = "Buscar CEST...",
  disabled,
}: Props) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["cest-combo-search", search],
    queryFn:  () => cestService.combo(search || undefined),
    enabled:  open,
    staleTime: 60_000,
  });
  const results = data ?? [];

  const { data: selecionado } = useQuery({
    queryKey: ["cest-combo-selected", value],
    queryFn:  () => cestService.combo(value ?? undefined),
    enabled:  value != null && value !== "",
    staleTime: 60_000,
    select:   (list) => list.find(c => c.codigo === value),
  });

  const label = selecionado
    ? `${selecionado.codigo} — ${selecionado.descricao ?? ""}`
    : value || placeholder;

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal h-8 text-sm"
            disabled={disabled}
            type="button"
          >
            <span className={cn("truncate text-left", !value && "text-muted-foreground")}>{label}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[460px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Digite código ou descrição do CEST..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {isLoading && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {!isLoading && <CommandEmpty>Nenhum CEST encontrado.</CommandEmpty>}
              <CommandGroup>
                {results.map(c => (
                  <CommandItem
                    key={c.id}
                    value={c.codigo ?? String(c.id)}
                    onSelect={() => { onChange(c.codigo ?? null); setOpen(false); }}
                  >
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === c.codigo ? "opacity-100" : "opacity-0")} />
                    <div className="flex flex-col min-w-0">
                      <span className="font-mono text-sm">{c.codigo}</span>
                      <span className="text-xs text-muted-foreground truncate">{c.descricao}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && !disabled && (
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" title="Limpar" onClick={() => onChange(null)}>
          <X className="h-4 w-4 text-muted-foreground" />
        </Button>
      )}
    </div>
  );
}
